/**
 * Performance Monitoring Hook
 * Provides easy access to performance metrics and monitoring functions
 */

import { useState, useEffect } from 'react';
import { performanceMonitor } from '@/lib/utils/performanceMonitor';
import { cacheService } from '@/lib/utils/cacheService';

interface PerformanceMetrics {
  general: Record<string, { avg: number; min: number; max: number; count: number }>;
  database: Record<string, { avg: number; min: number; max: number; count: number; slowQueries: number }>;
  cache: { 
    totalEntries: number; 
    expiredEntries: number;
    memoryUsage: string;
  };
  slowQueries: { query: string; duration: number; timestamp: number }[];
}

export function usePerformanceMonitoring() {
  const [metrics, setMetrics] = useState<PerformanceMetrics>({
    general: {},
    database: {},
    cache: { totalEntries: 0, expiredEntries: 0, memoryUsage: '0 MB' },
    slowQueries: []
  });
  const [isLoading, setIsLoading] = useState(false);

  const refreshMetrics = () => {
    setIsLoading(true);
    
    // Get all performance data
    const perfSummary = performanceMonitor.getSummary();
    const dbSummary = typeof performanceMonitor.getDBSummary === 'function'
      ? performanceMonitor.getDBSummary()
      : {};
    const cacheStats = cacheService.getStats();
    const slowQueries = performanceMonitor.getSlowQueriesReport();
    
    setMetrics({
      general: perfSummary,
      database: dbSummary,
      cache: cacheStats,
      slowQueries: slowQueries.slice(0, 20) // Limit to top 20
    });
    
    setIsLoading(false);
  };

  const clearAllData = () => {
    performanceMonitor.clear();
    cacheService.clear();
    refreshMetrics();
  };

  const logAllStats = () => {
    console.log('=== PERFORMANCE STATISTICS ===');
    performanceMonitor.logSummary();
    console.log('\n=== CACHE STATISTICS ===');
    cacheService.logStats();
    console.log('\n=== SLOW QUERIES ===');
    const slow = performanceMonitor.getSlowQueriesReport();
    slow.forEach((query, index) => {
      console.log(`${index + 1}. ${query.duration.toFixed(2)}ms - ${query.query.substring(0, 80)}...`);
    });
  };

  const getFormattedStats = () => {
    const stats = {
      performance: Object.entries(metrics.general).map(([key, data]) => ({
        operation: key,
        avg: `${data.avg.toFixed(2)}ms`,
        min: `${data.min.toFixed(2)}ms`,
        max: `${data.max.toFixed(2)}ms`,
        count: data.count
      })),
      database: Object.entries(metrics.database).map(([key, data]) => ({
        table: key.replace('db_', ''),
        avg: `${data.avg.toFixed(2)}ms`,
        min: `${data.min.toFixed(2)}ms`,
        max: `${data.max.toFixed(2)}ms`,
        queries: data.count,
        slow: data.slowQueries
      })),
      cache: {
        total: metrics.cache.totalEntries,
        active: metrics.cache.totalEntries - metrics.cache.expiredEntries,
        expired: metrics.cache.expiredEntries,
        memory: metrics.cache.memoryUsage
      }
    };
    
    return stats;
  };

  // Auto-refresh every 3 seconds when component is mounted
  useEffect(() => {
    const interval = setInterval(refreshMetrics, 3000);
    refreshMetrics(); // Initial load
    
    return () => {
      clearInterval(interval);
    };
  }, []);

  return {
    metrics,
    isLoading,
    refreshMetrics,
    clearAllData,
    logAllStats,
    getFormattedStats,
    // Direct access to services
    performanceMonitor,
    cacheService
  };
}

// Hook for monitoring specific operations
export function useOperationMonitor(operationName: string) {
  const [isRunning, setIsRunning] = useState(false);
  const [lastDuration, setLastDuration] = useState<number | null>(null);

  const startOperation = () => {
    setIsRunning(true);
    performanceMonitor.start(operationName);
  };

  const endOperation = () => {
    const duration = performanceMonitor.end(operationName);
    setLastDuration(duration);
    setIsRunning(false);
    return duration;
  };

  const measureAsync = async <T>(fn: () => Promise<T>): Promise<T> => {
    startOperation();
    try {
      const result = await fn();
      endOperation();
      return result;
    } catch (error) {
      endOperation();
      throw error;
    }
  };

  const measureSync = <T>(fn: () => T): T => {
    startOperation();
    try {
      const result = fn();
      endOperation();
      return result;
    } catch (error) {
      endOperation();
      throw error;
    }
  };

  return {
    isRunning,
    lastDuration,
    startOperation,
    endOperation,
    measureAsync,
    measureSync
  };
}

// Hook for cache monitoring
export function useCacheMonitor() {
  const [cacheStats, setCacheStats] = useState({
    totalEntries: 0,
    expiredEntries: 0,
    memoryUsage: '0 MB'
  });

  const refreshCacheStats = () => {
    setCacheStats(cacheService.getStats());
  };

  const clearCache = () => {
    cacheService.clear();
    refreshCacheStats();
  };

  const invalidatePattern = (pattern: string) => {
    const count = cacheService.invalidateByPattern(pattern);
    refreshCacheStats();
    return count;
  };

  // Auto-refresh cache stats
  useEffect(() => {
    const interval = setInterval(refreshCacheStats, 2000);
    refreshCacheStats();
    
    return () => {
      clearInterval(interval);
    };
  }, []);

  return {
    cacheStats,
    refreshCacheStats,
    clearCache,
    invalidatePattern,
    cacheService
  };
}